import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReconnectingWebSocket from "reconnecting-websocket";
import moment from "moment";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import { Turn, EvaluateResult } from "../../model_gen/session";
import { ChatCompletionRequestMetadata } from "../../model_gen/chat_metadata";
import { TestSet } from "../../model_gen/evaluation";
import TestSetContextMenu from "../TestSet/TestSetContextMenu";
import { RequestsService } from "../../services/RequestsService";
import { TestSetService } from "../../services/TestSetService";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "../ui/collapsible";

import { Tooltip } from "antd";
import {
  CopyIcon,
  CheckCircleIcon,
  InfoIcon,
  PlusIcon,
  ClockIcon,
  ExternalLinkIcon,
  BarChart3Icon,
  ServerIcon,
  NetworkIcon,
  PlayIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { message } from "antd";
import AgentContext, { SessionContext } from "./AgentContext";
import AgentTimelineCard from "./AgentTimelineCard";
import RoutingResultCard from "./RoutingResultCard";
import AgentTaskTreeModal from "./AgentTaskTreeModal";

const formatSessionId = (sessionId: string) => {
  if (!sessionId) return "";
  return `...${sessionId.slice(-8)}`;
};

// Reuse the same formatResponseContent function
const formatResponseContent = (text?: string): React.ReactNode => {
  if (!text) {
    return null;
  }

  // Check if the text contains [ or {
  if (text.includes("[") || text.includes("{")) {
    try {
      // Attempt to parse the text as JSON
      const parsedJson = JSON.parse(text);
      const formattedText = JSON.stringify(parsedJson, null, 2);
      return (
        <pre className="bg-muted/50 border border-border rounded-lg p-3 overflow-x-auto max-w-full">
          <code className="text-foreground text-sm whitespace-pre-wrap break-words">
            {formattedText}
          </code>
        </pre>
      );
    } catch (e) {
      // If parsing fails, return the original text
      return text;
    }
  } else {
    // If the text doesn't contain [ or {, return it as is
    return text;
  }
};

const AgentTaskView: React.FC = () => {
  const { agentId, sessionId, taskId } = useParams<{
    agentId: string;
    sessionId: string;
    taskId: string;
  }>();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [taskTurns, setTaskTurns] = useState<Turn[]>([]);
  const [contexts, setContexts] = useState<SessionContext[]>([]);
  const [loadingContexts, setLoadingContexts] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [testSetsByAction, setTestSetsByAction] = useState<
    Record<string, TestSet[]>
  >({});
  const [summaryLoading, setSummaryLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [requestMetadata, setRequestMetadata] = useState<{
    [key: string]: ChatCompletionRequestMetadata;
  }>({});
  const [selectedRequestId, setSelectedRequestId] = useState<
    string | undefined
  >(undefined);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const isManualSelectionRef = useRef(false);
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const lastMessageTimeRef = useRef<Date>(new Date());
  const [showGanttChart, setShowGanttChart] = useState(false);
  const [showTreeModal, setShowTreeModal] = useState(false);
  const [agentName, setAgentName] = useState<string>("");

  useEffect(() => {
    let cleanupWebsocket: (() => void) | undefined;

    if (agentId && sessionId && taskId) {
      loadTaskData();
      cleanupWebsocket = openWebsocket();
      // Fetch agent details
      AgentService.getAgent(Number(agentId), () => {}, apiKey).subscribe(
        (agent: any) => {
          console.log("Agent details fetched:", agent);
          setAgentName(agent.name || agent.agent_name || `Agent ${agentId}`);
        },
        (error: any) => {
          console.error("Error fetching agent details:", error);
          setAgentName(`Agent ${agentId}`);
        }
      );
    }

    return () => {
      if (cleanupWebsocket) {
        cleanupWebsocket();
      }
    };
  }, [agentId, sessionId, taskId, apiKey]);

  useEffect(() => {
    if (applicationId) {
      loadTestSets();
    }
  }, [applicationId, apiKey]);

  const loadTaskData = () => {
    setIsLoading(true);

    // Use the task timeline endpoint to get all turns for this task
    AgentService.getAgentTaskTimeline(
      Number(agentId),
      String(taskId || ""),
      () => {},
      apiKey
    ).subscribe(
      (data: any) => {
        if (data && Array.isArray(data.timeline)) {
          // Keep the nested structure from the backend
          setTaskTurns(data.timeline);

          // Flatten for extracting request IDs and application ID
          const flattenTurns = (turns: Turn[]): Turn[] => {
            const result: Turn[] = [];
            turns.forEach((turn) => {
              result.push(turn);
              if (
                (turn as any).sub_agents &&
                Array.isArray((turn as any).sub_agents)
              ) {
                result.push(...flattenTurns((turn as any).sub_agents));
              }
            });
            return result;
          };

          const flatTurns = flattenTurns(data.timeline);

          // Set application ID from the first turn
          if (flatTurns[0]?.request?.application_id) {
            setApplicationId(flatTurns[0].request.application_id);
          }

          // Load contexts for all requests in this task (flattened)
          const requestIds = flatTurns
            .map((turn: Turn) => turn.request?.request_id)
            .filter((id: string | undefined): id is string => !!id);

          if (requestIds.length > 0) {
            setLoadingContexts(true);
            AgentService.getRequestContextsByRequestIds(
              Number(agentId),
              requestIds,
              () => {},
              apiKey
            ).subscribe(
              (contextData: any) => {
                setContexts(contextData?.contexts || []);
                setLoadingContexts(false);
              },
              (error: any) => {
                console.error("Error fetching contexts:", error);
                setContexts([]);
                setLoadingContexts(false);
              }
            );
          } else {
            setContexts([]);
          }
        } else {
          message.warning("No timeline data found for this task");
          setTaskTurns([]);
          setContexts([]);
        }

        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error loading task data:", error);
        message.error("Failed to load task data");
        setIsLoading(false);
      }
    );
  };

  const openWebsocket = () => {
    closeWsConnection();

    const ws: any = new ReconnectingWebSocket(
      `wss://09hidyy627.execute-api.us-west-2.amazonaws.com/production?key=${encodeURIComponent(
        sessionId!
      )}&type=SESSION_UPDATE`
    );
    wsRef.current = ws;

    ws.addEventListener("message", (message: any) => {
      lastMessageTimeRef.current = new Date();
      try {
        const data = JSON.parse(message.data);
        if (data.event_type === "SESSION_UPDATE") {
          handleNewSessionTurn(data);
        }
      } catch (e) {
        console.error("Failed to parse WebSocket message:", e);
      }
    });

    const checkInterval = setInterval(() => {
      const now = new Date();
      const diffInMinutesWS =
        (now.getTime() - lastMessageTimeRef.current.getTime()) / (1000 * 60);

      if (diffInMinutesWS >= 1) {
        if (wsRef.current) {
          wsRef.current.reconnect(1000, "Manual reconnect");
          lastMessageTimeRef.current = new Date();
        }
      }
    }, 1000 * 5);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      closeWsConnection();
    };
  };

  const closeWsConnection = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const handleNewSessionTurn = (data: any) => {
    // When we get a SESSION_UPDATE, reload the task data to include new requests
    loadTaskData();
  };

  const loadTestSets = () => {
    TestSetService.getTestSets(null, apiKey).subscribe(
      (response: any) => {
        const testSets = response || [];
        const groupedTestSets = testSets.reduce(
          (acc: Record<string, TestSet[]>, testSet: TestSet) => {
            const actionType = testSet?.intent_group?.intent_name;
            if (!acc[actionType]) {
              acc[actionType] = [];
            }
            acc[actionType].push(testSet);
            return acc;
          },
          {}
        );
        setTestSetsByAction(groupedTestSets);
      },
      (error: any) => {
        console.error("Failed to load test sets:", error);
      }
    );
  };

  const copyRequestId = (requestId: string) => {
    navigator.clipboard.writeText(requestId);
    message.success("Request ID copied!");
  };

  const handleViewRequestClick = (requestId: string) => {
    const request = taskTurns.find(
      (t) => t.request?.request_id === requestId
    )?.request;
    const appId = applicationId || request?.application_id;

    if (appId) {
      window.open(
        `/application/${appId}/session/${encodeURIComponent(
          request?.session_id || ""
        )}/request/${requestId}`,
        "_blank"
      );
    }
  };

  const handleTestSetUpdate = (
    updatedRequestId: string,
    updatedTestSetIds: string[]
  ) => {
    setTaskTurns((prevTurns) => {
      return prevTurns.map((turn) => {
        if (turn.request?.request_id === updatedRequestId) {
          return {
            ...turn,
            request: {
              ...turn.request,
              metadata: {
                ...turn.request.metadata,
                test_set_ids: JSON.stringify(updatedTestSetIds),
              },
            },
          };
        }
        return turn;
      });
    });
    loadTestSets();
  };

  const fetchRequestSummary = (turn: Turn) => {
    const requestId = turn.request?.request_id;
    if (!requestId || requestMetadata[requestId]) return;

    setSummaryLoading((prev) => ({ ...prev, [requestId]: true }));

    RequestsService.getRequestMetadata(turn.request, null, apiKey).subscribe(
      (result: any) => {
        if (result.request_id) {
          setRequestMetadata((prev) => ({
            ...prev,
            [result.request_id]: result.metadata,
          }));
        }
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      },
      (error: any) => {
        console.error("Failed to fetch metadata:", error);
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      }
    );
  };

  const getExistingTestSetIds = (metadata?: any) => {
    try {
      return metadata?.test_set_ids
        ? JSON.parse(metadata.test_set_ids).map(String)
        : [];
    } catch (e) {
      console.error("Error parsing test_set_ids:", e);
      return [];
    }
  };

  const formatDateTime = (
    date: number | undefined,
    prevDate: number | undefined,
    nextDate: number | undefined
  ) => {
    if (!date) return { display: "", full: "" };
    const currentMoment = moment(date);

    // Always show full date for consistency with routing results
    const fullDateTime = currentMoment.format("MMM D, YYYY hh:mm:ss A");

    return { display: fullDateTime, full: fullDateTime };
  };

  // Set initial selected request when turns are loaded
  useEffect(() => {
    if (taskTurns.length > 0 && !selectedRequestId) {
      const firstRequest = taskTurns[0]?.request?.request_id;
      if (firstRequest) {
        setSelectedRequestId(firstRequest);
      }
    }
  }, [taskTurns, selectedRequestId]);

  // Clean up card refs for removed turns
  useEffect(() => {
    const currentRequestIds = new Set(
      taskTurns.map((t) => t.request?.request_id).filter(Boolean)
    );

    cardRefs.current.forEach((_, requestId) => {
      if (!currentRequestIds.has(requestId)) {
        cardRefs.current.delete(requestId);
      }
    });
  }, [taskTurns]);

  // Handle scroll-based selection
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Skip if manual selection is active
      if (isManualSelectionRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 3; // Use top third for selection

      let closestCard: { id: string; distance: number } | null = null;

      // Find the card closest to the container's top third
      cardRefs.current.forEach((cardElement, requestId) => {
        const cardRect = cardElement.getBoundingClientRect();
        const cardCenter = cardRect.top + cardRect.height / 2;
        const distance = Math.abs(cardCenter - containerCenter);

        // Only consider cards that are at least partially visible
        if (
          cardRect.bottom > containerRect.top &&
          cardRect.top < containerRect.bottom
        ) {
          if (!closestCard || distance < closestCard.distance) {
            closestCard = { id: requestId, distance };
          }
        }
      });

      if (closestCard && closestCard.id !== selectedRequestId) {
        setSelectedRequestId(closestCard.id);
      }
    };

    // Use requestAnimationFrame for smooth updates during scrolling
    let ticking = false;
    const handleScrollRAF = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener("scroll", handleScrollRAF, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScrollRAF);
    };
  }, [selectedRequestId, taskTurns]);

  // Auto-scroll only for manual selections (clicks)
  useEffect(() => {
    if (!selectedRequestId || !scrollContainerRef.current) return;

    // Only auto-scroll for manual selections
    if (!isManualSelectionRef.current) return;

    const selectedCard = cardRefs.current.get(selectedRequestId);
    if (!selectedCard) return;

    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const cardRect = selectedCard.getBoundingClientRect();

    // Always scroll clicked cards into view at the top third of the viewport
    const scrollTop =
      container.scrollTop +
      (cardRect.top - containerRect.top) +
      cardRect.height / 2 -
      containerRect.height / 3;
    container.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });

    // Reset manual selection flag after scrolling
    setTimeout(() => {
      isManualSelectionRef.current = false;
    }, 500);
  }, [selectedRequestId]);

  // For displaying total count, flatten to count all items
  const totalItemCount = React.useMemo(() => {
    const flatten = (turns: Turn[]): number => {
      let count = 0;
      turns.forEach((turn) => {
        count++;
        if (
          (turn as any).sub_agents &&
          Array.isArray((turn as any).sub_agents)
        ) {
          count += flatten((turn as any).sub_agents);
        }
      });
      return count;
    };
    return flatten(taskTurns);
  }, [taskTurns]);

  // Track which sub-agent delegations are expanded
  const [expandedSubAgents, setExpandedSubAgents] = React.useState<Set<string>>(
    new Set()
  );

  // Recursive function to render timeline with nested sub-agents
  const renderTimelineItems = (
    turns: Turn[],
    depth: number = 0
  ): JSX.Element[] => {
    return turns.map((turn, index) => {
      const leftPadding = depth * 32;

      // Handle SUB_AGENT_DELEGATION as collapsible container
      if ((turn.request as any)?.agent_action_type === "SUB_AGENT_DELEGATION") {
        const requestId = turn.request?.request_id || "";
        const isExpanded = expandedSubAgents.has(requestId);
        const subAgents = (turn as any).sub_agents || [];

        return (
          <div
            key={requestId}
            style={{ marginLeft: `${leftPadding}px` }}
            className="relative pl-8">
            {/* Timeline dot */}
            <div className="absolute left-1.5 top-7">
              <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-background absolute transform -translate-x-1/2 -translate-y-1/2" />
            </div>

            {/* Timeline line */}
            {index < turns.length - 1 && (
              <div
                className="absolute left-1.5 w-px bg-border transition-all duration-200"
                style={{
                  top: "40px",
                  height: isExpanded
                    ? "calc(100% - 60px)"
                    : "calc(100% - 40px)",
                }}
              />
            )}

            <Collapsible
              open={isExpanded}
              onOpenChange={(open) => {
                setExpandedSubAgents((prev) => {
                  const newSet = new Set(prev);
                  if (open) {
                    newSet.add(requestId);
                  } else {
                    newSet.delete(requestId);
                  }
                  return newSet;
                });
              }}>
              <Card className="border-border hover:border-maitai-green/50 transition-colors">
                <CollapsibleTrigger className="w-full text-left">
                  <CardContent
                    className={cn(
                      "transition-all",
                      isExpanded ? "p-4" : "py-3 px-4"
                    )}>
                    <div
                      className={cn("space-y-3", !isExpanded && "space-y-0")}>
                      {/* Always visible header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                          <span className="text-xs text-muted-foreground">
                            {turn.request?.date_created
                              ? moment(turn.request.date_created).format(
                                  "MMM D, YYYY h:mm:ss A"
                                )
                              : ""}
                          </span>
                          <PlayIcon className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
                          <Badge
                            variant="outline"
                            className="text-xs text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800">
                            Action
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                            Sub-agent
                          </Badge>
                          {!isExpanded && (
                            <span className="text-sm font-gc-med text-foreground truncate">
                              {(turn.request as any).agent_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(turn.request as any).execution_time_ms &&
                            !isExpanded && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <ClockIcon className="w-3 h-3" />
                                {(turn.request as any).execution_time_ms}ms
                              </span>
                            )}
                        </div>
                      </div>

                      {/* Only show when expanded */}
                      {isExpanded && (
                        <>
                          <div className="text-sm font-gc-med text-foreground">
                            {(turn.request as any).agent_name}
                          </div>
                          {(turn.request as any).agent_description && (
                            <p className="text-sm text-muted-foreground">
                              {(turn.request as any).agent_description}
                            </p>
                          )}

                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {(turn.request as any).execution_time_ms && (
                              <span className="flex items-center gap-1">
                                <ClockIcon className="w-3 h-3" />
                                {(turn.request as any).execution_time_ms}ms
                              </span>
                            )}
                            {(turn.request as any).delegation_intent && (
                              <span>
                                Intent:{" "}
                                {(turn.request as any).delegation_intent}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </CollapsibleTrigger>

                {/* Nested sub-agent actions */}
                <CollapsibleContent>
                  <div className="border-t border-border">
                    <div className="space-y-6 p-4">
                      {renderTimelineItems(subAgents, depth + 1)}
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>
        );
      }

      // For other turn types, use existing rendering logic
      // This will be the routing results and regular actions
      return renderRegularTurn(turn, index, depth, leftPadding, turns.length);
    });
  };

  // Render a regular turn (routing or action)
  const renderRegularTurn = (
    turn: Turn,
    index: number,
    depth: number,
    leftPadding: number,
    totalItems: number
  ): JSX.Element => {
    // Handle routing results
    if ((turn as any).routing_result) {
      const routingResult = (turn as any).routing_result;
      return (
        <div
          key={`routing-${routingResult.id}`}
          style={{ marginLeft: `${leftPadding}px` }}>
          <RoutingResultCard
            routingResult={routingResult}
            turnIndex={index}
            totalTurns={totalItems}
            agentId={Number(agentId)}
          />
        </div>
      );
    }

    // Handle regular actions (LLM_CALL, API_CALL, etc.) - FULL IMPLEMENTATION
    const requestId = turn.request?.request_id || `turn-${index}`;

    // Parse metadata
    let requestMetadataLocal: any = {};
    try {
      requestMetadataLocal = JSON.parse(
        (turn.request?.metadata?._maitai_request_meta as string) || "{}"
      );
    } catch (e) {
      console.error("Failed to parse _maitai_request_meta:", e);
      requestMetadataLocal = {};
    }
    const metadata =
      turn.request?.request_id && requestMetadata[turn.request.request_id]
        ? requestMetadata[turn.request.request_id]
        : requestMetadataLocal;

    // Count faults
    let faultCount = 0;
    turn.eval_request?.eval_results_set?.evaluation_results?.forEach(
      (evalResult: EvaluateResult) => {
        if (evalResult.status === "FAULT") {
          faultCount += 1;
        }
      }
    );

    return (
      <div
        key={requestId}
        data-request-id={turn.request?.request_id}
        style={{ marginLeft: `${leftPadding}px` }}
        className="relative pl-8">
        {/* Timeline dot */}
        <div className="absolute left-1.5 top-7">
          <div
            className={cn(
              "rounded-full border-2 transition-all duration-200 absolute transform -translate-x-1/2 -translate-y-1/2",
              expandedCards.has(requestId) ? "w-5 h-5 shadow-lg" : "w-4 h-4",
              "border-background",
              faultCount > 0
                ? "bg-red-500"
                : turn.response?.correction_applied
                ? "bg-green-500"
                : "bg-maitai-green"
            )}
          />
        </div>

        {/* Timeline line */}
        {index < totalItems - 1 && (
          <div
            className="absolute left-1.5 w-px bg-border transition-all duration-200"
            style={{
              top: "40px",
              height: "calc(100% - 60px)",
            }}
          />
        )}

        <Card
          ref={(el) => {
            if (el && turn.request?.request_id) {
              cardRefs.current.set(turn.request.request_id, el);
            }
          }}
          className={cn(
            "w-full cursor-pointer transition-all hover:shadow-md",
            turn.request?.request_id === selectedRequestId
              ? "border-[1.5px] border-maitai-green shadow-lg"
              : "border border-border"
          )}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (
              target.closest("button") ||
              target.closest("a") ||
              target.closest("[role=\"button\"]")
            ) {
              return;
            }
            if (turn.request?.request_id) {
              isManualSelectionRef.current = true;
              setSelectedRequestId(turn.request.request_id);
            }
          }}>
          <Collapsible
            open={expandedCards.has(requestId)}
            onOpenChange={(isOpen) => {
              setExpandedCards((prev) => {
                const newSet = new Set(prev);
                if (isOpen) {
                  newSet.add(requestId);
                } else {
                  newSet.delete(requestId);
                }
                return newSet;
              });
            }}>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground">
                      {turn.request?.date_created
                        ? moment(turn.request.date_created).format(
                            "MMM D, YYYY h:mm:ss A"
                          )
                        : ""}
                    </span>
                    <PlayIcon className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
                    <Badge
                      variant="outline"
                      className="text-xs text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800">
                      Action
                    </Badge>
                    {turn.request?.agent_action_type && (
                      <Badge
                        variant="secondary"
                        className={cn(
                          "text-xs",
                          turn.request.agent_action_type === "API_CALL"
                            ? "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
                            : "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                        )}>
                        {turn.request.agent_action_type === "API_CALL"
                          ? "API"
                          : "LLM"}
                      </Badge>
                    )}
                    {turn.request?.action_type && (
                      <Badge variant="outline" className="text-xs">
                        {turn.request.action_type}
                      </Badge>
                    )}
                    {turn.response?.correction_applied ? (
                      <Badge
                        variant="default"
                        className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 text-xs">
                        Corrected
                      </Badge>
                    ) : faultCount > 0 ? (
                      <Badge variant="destructive" className="text-xs">
                        {faultCount > 1 ? "FAULTS" : "FAULT"}
                      </Badge>
                    ) : (
                      <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Tooltip title="View Request Details">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewRequestClick(
                            turn.request?.request_id || ""
                          );
                        }}>
                        <ExternalLinkIcon className="w-3 h-3" />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Copy Request ID">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyRequestId(turn.request?.request_id || "");
                        }}>
                        <CopyIcon className="w-3 h-3" />
                      </Button>
                    </Tooltip>
                    {turn.request && applicationId && (
                      <TestSetContextMenu
                        applicationId={applicationId.toString()}
                        applicationActionType={turn.request.action_type || ""}
                        requestId={turn.request.request_id || ""}
                        testSets={
                          turn.request.action_type
                            ? [
                                ...(testSetsByAction[
                                  turn.request.action_type
                                ] || []),
                              ].sort((a, b) => b.id - a.id)
                            : []
                        }
                        existingTestSetIds={getExistingTestSetIds(
                          turn.request.metadata
                        )}
                        onUpdate={handleTestSetUpdate}>
                        <Tooltip title="Add to Test Set">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}>
                            <PlusIcon className="w-3 h-3" />
                          </Button>
                        </Tooltip>
                      </TestSetContextMenu>
                    )}
                    <CollapsibleTrigger className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 p-0">
                      <></>
                    </CollapsibleTrigger>
                  </div>
                </div>

                {/* Response content */}
                <div className="space-y-3">
                  {metadata?.summary && (
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <InfoIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-900 dark:text-blue-100">
                          {metadata.summary}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2 min-w-0">
                    {(turn.response as any)?.response_body ? (
                      <div
                        className={cn(
                          "relative text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words overflow-hidden",
                          !expandedCards.has(requestId) &&
                            (turn.request as any)?.agent_action_type ===
                              "API_CALL" &&
                            "max-h-32"
                        )}>
                        {formatResponseContent(
                          String((turn.response as any).response_body)
                        )}
                        {!expandedCards.has(requestId) &&
                          (turn.request as any)?.agent_action_type ===
                            "API_CALL" && (
                            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background to-transparent pointer-events-none" />
                          )}
                      </div>
                    ) : turn.response?.response ? (
                      <div
                        className={cn(
                          "relative text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words overflow-hidden",
                          !expandedCards.has(requestId) && "max-h-32"
                        )}>
                        {formatResponseContent(String(turn.response.response))}
                        {!expandedCards.has(requestId) && (
                          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background to-transparent pointer-events-none" />
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground italic text-sm">
                        {(turn.response as any)?.feedback ||
                          "No response content"}
                      </span>
                    )}
                    {turn.request?.error && (
                      <div className="p-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded text-red-900 dark:text-red-100 text-sm break-words">
                        {turn.request.error}
                      </div>
                    )}
                    {(turn.response as any)?.error_message && (
                      <div className="p-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded text-red-900 dark:text-red-100 text-sm break-words">
                        {String((turn.response as any).error_message)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata row */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {(() => {
                    const responseTime =
                      turn.response?.response_time ??
                      (turn.response as any)?.response_time_ms;
                    const firstTokenTime = (turn.response as any)
                      ?.first_token_time;

                    if (responseTime == null) return null;

                    if (
                      (turn.request as any)?.agent_action_type === "LLM_CALL" &&
                      firstTokenTime != null &&
                      Number(firstTokenTime) > 0
                    ) {
                      return (
                        <span className="flex items-center gap-1">
                          <ClockIcon className="w-3 h-3" />
                          {firstTokenTime}/{responseTime}ms
                        </span>
                      );
                    }

                    return (
                      <span className="flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        {responseTime}ms
                      </span>
                    );
                  })()}

                  {(turn.request as any)?.agent_action_type === "LLM_CALL" &&
                    turn.response?.prompt_tokens != null &&
                    turn.response?.completion_tokens != null && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <ServerIcon className="w-3 h-3" />
                        {String(turn.response.prompt_tokens)} →{" "}
                        {String(turn.response.completion_tokens)}
                      </span>
                    )}

                  {(turn.request as any)?.agent_action_type === "API_CALL" &&
                    (turn.response as any)?.status_code && (
                      <span
                        className={cn(
                          "flex items-center gap-1 font-mono",
                          Number((turn.response as any).status_code) >= 200 &&
                            Number((turn.response as any).status_code) < 300
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        )}>
                        HTTP {(turn.response as any).status_code}
                      </span>
                    )}
                </div>

                {/* Expandable technical details */}
                <CollapsibleContent>
                  <div className="pt-4 border-t border-border">
                    <h4 className="text-sm font-gc-med text-foreground mb-3">
                      Technical Details
                    </h4>

                    {(turn.request as any)?.agent_action_type ===
                      "LLM_CALL" && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div className="space-y-1">
                          <p className="text-muted-foreground font-gc-reg">
                            Model
                          </p>
                          <p className="text-foreground">
                            {turn.request?.params?.model ||
                              turn.response?.model ||
                              "-"}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-muted-foreground font-gc-reg">
                            Temperature
                          </p>
                          <p className="text-foreground">
                            {turn.request?.params?.temperature !== undefined
                              ? turn.request.params.temperature
                              : "-"}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-muted-foreground font-gc-reg">
                            Streaming
                          </p>
                          <p className="text-foreground">
                            {turn.request?.params?.stream ? "On" : "Off"}
                          </p>
                        </div>
                      </div>
                    )}

                    {(turn.request as any)?.agent_action_type ===
                      "API_CALL" && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div className="space-y-1">
                          <p className="text-muted-foreground font-gc-reg">
                            HTTP Method
                          </p>
                          <p className="text-foreground font-mono">
                            {(turn.request as any)?.http_method || "-"}
                          </p>
                        </div>
                        <div className="space-y-1 col-span-2">
                          <p className="text-muted-foreground font-gc-reg">
                            URL
                          </p>
                          <p className="text-foreground font-mono text-xs break-all">
                            {(turn.request as any)?.url || "-"}
                          </p>
                        </div>
                        {(turn.request as any)?.query_params &&
                          Object.keys((turn.request as any).query_params)
                            .length > 0 && (
                            <div className="space-y-1 col-span-3">
                              <p className="text-muted-foreground font-gc-reg">
                                Query Params
                              </p>
                              <pre className="text-foreground font-mono text-xs bg-muted/50 p-2 rounded overflow-x-auto">
                                {JSON.stringify(
                                  (turn.request as any).query_params,
                                  null,
                                  2
                                )}
                              </pre>
                            </div>
                          )}
                        {(turn.request as any)?.headers &&
                          Object.keys((turn.request as any).headers).length >
                            0 && (
                            <div className="space-y-1 col-span-3">
                              <p className="text-muted-foreground font-gc-reg">
                                Request Headers
                              </p>
                              <pre className="text-foreground font-mono text-xs bg-muted/50 p-2 rounded overflow-x-auto max-h-32">
                                {JSON.stringify(
                                  (turn.request as any).headers,
                                  null,
                                  2
                                )}
                              </pre>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </CardContent>
          </Collapsible>
        </Card>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] -m-6">
        {/* Left side - Task Timeline Skeleton */}
        <div className="flex-1 flex flex-col border-r border-border bg-background h-full">
          {/* Fixed Header Section */}
          <div className="p-6 space-y-6">
            {/* Breadcrumbs Skeleton */}
            <div className="flex items-center space-x-2">
              <div className="h-4 w-12 bg-muted rounded animate-pulse" />
              <div className="h-4 w-2 bg-muted rounded animate-pulse" />
              <div className="h-4 w-16 bg-muted rounded animate-pulse" />
              <div className="h-4 w-2 bg-muted rounded animate-pulse" />
              <div className="h-4 w-20 bg-muted rounded animate-pulse" />
              <div className="h-4 w-2 bg-muted rounded animate-pulse" />
              <div className="h-4 w-12 bg-muted rounded animate-pulse" />
            </div>

            {/* Header Skeleton */}
            <div className="h-10 w-80 bg-muted rounded animate-pulse" />

            {/* Task Information Card Skeleton */}
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                      <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Scrollable Timeline Section Skeleton */}
          <div className="flex-1 overflow-auto px-6 pb-6">
            <div className="space-y-6">
              {/* Timeline Header Skeleton */}
              <div className="sticky top-0 bg-background z-10 -mx-6 px-6 pt-3">
                <div className="flex items-center gap-2 py-3">
                  <div className="h-5 w-5 bg-muted rounded animate-pulse" />
                  <div className="h-6 w-32 bg-muted rounded animate-pulse" />
                </div>
              </div>

              {/* Timeline Cards Skeleton */}
              <div className="space-y-6 mt-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="relative pl-8">
                    {/* Timeline dot */}
                    <div className="absolute left-1.5 top-7 w-4 h-4 bg-muted rounded-full animate-pulse" />

                    {/* Timeline line */}
                    {i < 3 && (
                      <div className="absolute left-1.5 top-10 w-px h-32 bg-muted animate-pulse" />
                    )}

                    <Card className="border-border">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {/* Header skeleton */}
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 flex-1">
                              <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                              <div className="h-5 w-24 bg-muted rounded animate-pulse" />
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="h-7 w-7 bg-muted rounded animate-pulse" />
                              <div className="h-7 w-7 bg-muted rounded animate-pulse" />
                              <div className="h-7 w-7 bg-muted rounded animate-pulse" />
                            </div>
                          </div>

                          {/* Content skeleton */}
                          <div className="space-y-2">
                            <div className="h-4 w-full bg-muted rounded animate-pulse" />
                            <div className="h-4 w-4/5 bg-muted rounded animate-pulse" />
                            <div className="h-4 w-3/5 bg-muted rounded animate-pulse" />
                          </div>

                          {/* Metadata skeleton */}
                          <div className="flex items-center gap-4 pt-2 border-t border-border/50">
                            <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                            <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Context Skeleton */}
        <div className="w-[400px] h-full overflow-auto bg-muted/20">
          <div className="p-4">
            <div className="h-5 w-32 bg-muted rounded animate-pulse mb-4" />
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <Card key={i} className="border-border">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                      <div className="h-4 w-4 bg-muted rounded animate-pulse" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-muted rounded animate-pulse" />
                      <div className="h-3 w-4/5 bg-muted rounded animate-pulse" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] -m-6">
      {/* Left side - Task Timeline */}
      <div className="flex-1 flex flex-col border-r border-border bg-background h-full">
        {/* Fixed Header Section */}
        <div className="p-6 space-y-6">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <button
                  onClick={() => navigate("/")}
                  className="text-muted-foreground hover:text-foreground transition-colors">
                  Home
                </button>
              </li>
              <li className="text-muted-foreground">/</li>
              <li>
                <button
                  onClick={() => navigate("/agents")}
                  className="text-muted-foreground hover:text-foreground transition-colors">
                  Agents
                </button>
              </li>
              <li className="text-muted-foreground">/</li>
              <li>
                <button
                  onClick={() => navigate(`/agent/${agentId}`)}
                  className="text-muted-foreground hover:text-foreground transition-colors">
                  {agentName || `Agent ${agentId}`}
                </button>
              </li>
              <li className="text-muted-foreground">/</li>
              <li>
                <span className="text-maitai-green font-medium">Task</span>
              </li>
            </ol>
          </nav>

          {/* Header */}
          <h1 className="text-4xl font-gc-demi-bold text-foreground">
            Agent Task Details
          </h1>

          {/* Task Information */}
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Session ID</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                      {formatSessionId(sessionId || "")}
                    </code>
                    <Tooltip title="Copy Session ID">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText(sessionId || "");
                          message.success("Session ID copied!");
                        }}>
                        <CopyIcon className="w-3 h-3" />
                      </Button>
                    </Tooltip>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Start Time</p>
                  <p className="text-sm font-gc-med">
                    {(() => {
                      // Look for task_start_time in any turn that has it
                      const turnWithStartTime = taskTurns.find(
                        (turn) => turn.request?.task_start_time
                      );
                      if (turnWithStartTime?.request?.task_start_time) {
                        return moment(
                          turnWithStartTime.request.task_start_time
                        ).format("MMM D, h:mm:ss A");
                      }
                      // Fallback to first request's date_created if no task_start_time
                      if (taskTurns[0]?.request?.date_created) {
                        return moment(taskTurns[0].request.date_created).format(
                          "MMM D, h:mm:ss A"
                        );
                      }
                      return "-";
                    })()}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Task Duration</p>
                  <p className="text-sm font-gc-med">
                    {(() => {
                      // Look for execution_time_ms in any turn that has it
                      const turnWithDuration = taskTurns.find(
                        (turn) => turn.request?.execution_time_ms != null
                      );
                      if (
                        turnWithDuration?.request?.execution_time_ms != null
                      ) {
                        return `${turnWithDuration.request.execution_time_ms}ms`;
                      }
                      // Fallback: calculate duration from first to last request if no execution_time_ms
                      if (
                        taskTurns.length > 0 &&
                        taskTurns[0]?.request?.date_created &&
                        taskTurns[taskTurns.length - 1]?.request?.date_created
                      ) {
                        const startTime = taskTurns[0].request.date_created;
                        const endTime =
                          taskTurns[taskTurns.length - 1].request.date_created;
                        const duration = endTime - startTime;
                        return `${duration}ms`;
                      }
                      return "-";
                    })()}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Total Requests
                  </p>
                  <p className="text-sm font-gc-med">{totalItemCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scrollable Timeline Section */}
        <div
          className="flex-1 overflow-auto px-6 pb-6"
          ref={scrollContainerRef}>
          <div>
            {/* Combined Sticky Header Section */}
            <div className="sticky top-0 z-10 bg-background -mx-6 px-6 pt-3">
              {/* Request Timeline Header with Gantt and Tree toggles */}
              <div className="flex items-center gap-2 py-3">
                <h2 className="text-lg font-gc-med text-foreground flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 text-maitai-green" />
                  Request Timeline
                </h2>

                {/* Gantt Chart Toggle */}
                <Tooltip
                  title={
                    showGanttChart
                      ? "Hide Timeline Visualization"
                      : "Show Timeline Visualization"
                  }>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setShowGanttChart(!showGanttChart)}>
                    <BarChart3Icon
                      className={cn(
                        "w-4 h-4",
                        showGanttChart
                          ? "text-maitai-green"
                          : "text-muted-foreground"
                      )}
                    />
                  </Button>
                </Tooltip>

                {/* Agent Tree Toggle */}
                <Tooltip title="Show Agent Delegation Tree">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setShowTreeModal(true)}>
                    <NetworkIcon className="w-4 h-4 text-muted-foreground hover:text-maitai-green" />
                  </Button>
                </Tooltip>
              </div>

              {/* Collapsible Gantt Chart */}
              {showGanttChart && (
                <div className="pb-3">
                  <AgentTimelineCard
                    contexts={contexts}
                    turns={(() => {
                      const flatten = (turns: Turn[]): Turn[] => {
                        const result: Turn[] = [];
                        turns.forEach((turn) => {
                          result.push(turn);
                          if ((turn as any).sub_agents) {
                            result.push(...flatten((turn as any).sub_agents));
                          }
                        });
                        return result;
                      };
                      return flatten(taskTurns);
                    })()}
                  />
                </div>
              )}
            </div>

            <div className="space-y-6 mt-6">
              {renderTimelineItems(taskTurns, 0)}
            </div>

            {/* Listening indicator */}
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 bg-maitai-green rounded-full animate-pulse"></div>
                Listening for new requests...
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Contexts */}
      <div className="w-[400px] h-full overflow-auto bg-muted/20">
        <div className="p-4">
          <AgentContext
            contexts={contexts}
            selectedRequestId={selectedRequestId}
            showHeader={true}
            isLoading={loadingContexts}
          />
        </div>
      </div>

      {/* Agent Task Tree Modal */}
      <AgentTaskTreeModal
        taskId={taskId || null}
        isOpen={showTreeModal}
        onClose={() => setShowTreeModal(false)}
      />
    </div>
  );
};

export default AgentTaskView;
